import { isMissingPracticeRoute } from '../api/hooks';
import {
  LiveChannel,
  LiveCryptoError,
  deriveSessionKey,
  generateEphemeralKey,
  keyIdOf,
  verifyHello,
  type EphemeralKey,
} from './crypto';
import type {
  LiveLogEntry,
  LiveOpenAnswer,
  LiveOpenRequest,
  LiveRelayMessage,
  LiveRequest,
  LiveRowsAnswer,
  LiveRun,
  LiveSqlResult,
  LiveSqlValue,
} from './types';

// One live view session: open it through Rudi, wait for the practice
// computer's signed hello, then send one encrypted request at a time and
// long-poll for the answers. Rudi relays ciphertext; the plaintext lives only
// in this object and in the page that holds it.

export const LIVE_PATH = '/cortex/practice-agents/live';
/** Cortex's proxy gives up after 60 s, so the browser waits at most 25 s per poll. */
export const POLL_WAIT_SECONDS = 25;
const POLL_TIMEOUT_MS = 45_000;
const POLL_ATTEMPTS = 3;
export const MAX_SESSION_MS = 60 * 60_000;
export const HELLO_TIMEOUT_MS = 90_000;
/** The practice stops a SQL statement after 5 minutes; allow for the relay on top. */
export const ANSWER_TIMEOUT_MS = 6 * 60_000;
const SESSION_ID = /^[A-Za-z0-9_-]{1,128}$/;

export type LiveViewErrorCode =
  | 'unavailable'
  | 'live_view_off'
  | 'device_offline'
  | 'refused'
  | 'signature'
  | 'unsupported'
  | 'no_answer'
  | 'protocol'
  | 'ended'
  | 'expired'
  | 'relay'
  | 'log_failed'
  | 'aborted'
  | 'closed'
  | 'failed';

/** Why a live view could not open, or why it ended. The message is for the operator. */
export class LiveViewError extends Error {
  readonly code: LiveViewErrorCode;
  constructor(code: LiveViewErrorCode, message: string) {
    super(message);
    this.name = 'LiveViewError';
    this.code = code;
  }
}

/** The practice computer answered a request with `{op: "error", errorClass}`. */
export class LiveDeviceError extends Error {
  readonly errorClass: string;
  constructor(errorClass: string) {
    super(`The practice computer answered with error ${errorClass}.`);
    this.name = 'LiveDeviceError';
    this.errorClass = errorClass;
  }
}

export interface LiveSession {
  readonly sessionId: string;
  /** Epoch ms: Rudi's expiry or 60 minutes after opening, whichever comes first. */
  readonly closesAt: number;
  /** Key ID of the device key that signed the hello, as Rudi shows it. */
  readonly deviceKeyId: string;
  runs(): Promise<LiveRun[]>;
  rows(input: {
    runId: string;
    cursor?: string;
    excludedSample?: boolean;
  }): Promise<LiveRowsAnswer>;
  /** Logs the statement in Cortex first and sends nothing if that fails. */
  sql(statement: string): Promise<LiveSqlResult>;
  /** Tells the practice computer and Rudi, then drops the key. Safe to call twice. */
  close(): Promise<void>;
}

export interface OpenLiveSessionOptions {
  /** Aborting while the session opens closes it at Rudi again. */
  signal?: AbortSignal;
  /** Rudi opened the session; the practice computer has not answered yet. */
  onWaiting?: () => void;
  /** After the session opened: null when closed on purpose, else why it ended. */
  onEnd?: (error: LiveViewError | null) => void;
}

/** The part of the Axios client a session uses. */
export interface LiveHttp {
  get(
    url: string,
    config?: { params?: Record<string, number>; signal?: AbortSignal; timeout?: number }
  ): Promise<{ data: unknown }>;
  post(url: string, body?: unknown, config?: { signal?: AbortSignal }): Promise<{ data: unknown }>;
}

type ErrorBody = { error?: unknown; detail?: unknown };

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

function codeOf(error: unknown): string | undefined {
  const data = (error as { response?: { data?: ErrorBody } })?.response?.data;
  const value = data?.error ?? data?.detail;
  return typeof value === 'string' ? value : undefined;
}

function rudiText(error: unknown, fallback: string): string {
  const code = codeOf(error);
  if (code) return code;
  return error instanceof Error && error.message ? error.message : fallback;
}

function openError(error: unknown): LiveViewError {
  if (error instanceof LiveViewError) return error;
  const status = statusOf(error);
  const code = codeOf(error);
  if (status === 409 && code === 'live_view_off')
    return new LiveViewError(
      'live_view_off',
      'The practice has switched the live view off on its computer. Only the practice can switch it back on, in its practice data settings.'
    );
  if (status === 409 && code === 'device_offline')
    return new LiveViewError(
      'device_offline',
      'The practice computer is offline. Rudi has not heard from it in the last 15 minutes.'
    );
  if (isMissingPracticeRoute(error, 'write'))
    return new LiveViewError('unavailable', 'This Rudi release has no live view yet.');
  if (status === 403)
    return new LiveViewError(
      'failed',
      'Rudi refused the live view. It is open to super admins with admin:system only.'
    );
  return new LiveViewError('failed', rudiText(error, 'Rudi could not open the live view.'));
}

function cryptoError(error: unknown): LiveViewError {
  if (error instanceof LiveViewError) return error;
  if (error instanceof LiveCryptoError) {
    if (error.reason === 'unsupported') return new LiveViewError('unsupported', error.message);
    if (error.reason === 'signature')
      return new LiveViewError(
        'signature',
        "The practice computer's signature did not match its device key, so Cortex closed the live view before anything was shown."
      );
    return new LiveViewError(
      'protocol',
      `The practice computer sent a message Cortex could not check, so Cortex closed the live view. ${error.message}`
    );
  }
  return new LiveViewError(
    'failed',
    error instanceof Error ? error.message : 'The live view failed.'
  );
}

function protocolError(detail: string): LiveViewError {
  return new LiveViewError(
    'protocol',
    `The practice computer sent a message Cortex could not use, so Cortex closed the live view. ${detail}`
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function relayMessages(data: unknown): LiveRelayMessage[] {
  // A long poll that ran out without news may answer with no body at all.
  if (data === '' || data === null || data === undefined) return [];
  const list = isRecord(data) ? data.messages : undefined;
  if (!Array.isArray(list)) throw protocolError('Rudi sent no message list.');
  return list.map(item => {
    if (
      !isRecord(item) ||
      !Number.isSafeInteger(item.seq) ||
      !['hello', 'data', 'close'].includes(item.kind as string)
    )
      throw protocolError('Rudi relayed a malformed message.');
    return item as unknown as LiveRelayMessage;
  });
}

function sqlValue(value: unknown): LiveSqlValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value;
  return JSON.stringify(value);
}

/** Rows as arrays in column order, whether the practice sent arrays or objects. */
function sqlResult(answer: Record<string, unknown>): LiveSqlResult {
  const columns = Array.isArray(answer.columns) ? answer.columns.map(String) : [];
  const rows = Array.isArray(answer.rows) ? answer.rows : [];
  return {
    op: 'sql_result',
    columns,
    rows: rows.map(row =>
      Array.isArray(row)
        ? row.map(sqlValue)
        : isRecord(row)
          ? columns.map(column => sqlValue(row[column]))
          : [sqlValue(row)]
    ),
    truncated: answer.truncated === true,
  };
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new LiveViewError('aborted', 'The live view closed.'));
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new LiveViewError('aborted', 'The live view closed.'));
      },
      { once: true }
    );
  });
}

type AnswerOp = 'runs' | 'rows' | 'sql_result';

interface Pending {
  expect: AnswerOp;
  resolve: (answer: Record<string, unknown>) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

class Session implements LiveSession {
  readonly sessionId: string;
  readonly closesAt: number;
  deviceKeyId = '';
  private readonly base: string;
  private readonly polls = new AbortController();
  private channel: LiveChannel | null = null;
  private ready = false;
  private ended = false;
  /** The last relay number seen from the device; the hello is 0. */
  private lastSeq: number | null = null;
  private backlog: LiveRelayMessage[] = [];
  private pending: Pending | null = null;
  private requests: Promise<unknown> = Promise.resolve();
  private posts: Promise<unknown> = Promise.resolve();
  private expiry: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly http: LiveHttp,
    private readonly opened: LiveOpenAnswer,
    private ephemeral: EphemeralKey | null,
    private readonly options: OpenLiveSessionOptions
  ) {
    this.sessionId = opened.sessionId;
    this.base = `${LIVE_PATH}/${opened.sessionId}`;
    const expires = Date.parse(opened.expiresAt);
    this.closesAt = Math.min(
      Date.now() + MAX_SESSION_MS,
      Number.isFinite(expires) ? expires : Infinity
    );
    options.signal?.addEventListener('abort', () => this.polls.abort(), { once: true });
  }

  async start(): Promise<void> {
    try {
      await this.http.post(`${this.base}/log`, { kind: 'page' } satisfies LiveLogEntry);
    } catch (error) {
      if (this.polls.signal.aborted) throw new LiveViewError('aborted', 'The live view closed.');
      throw new LiveViewError(
        'log_failed',
        `Cortex could not log this page view, so it closed the live view. ${rudiText(error, '')}`.trim()
      );
    }
    this.options.onWaiting?.();
    const hello = await this.waitForHello();
    const ephemeral = this.ephemeral!;
    try {
      await verifyHello({
        sessionId: this.sessionId,
        browserPublicKey: ephemeral.publicKey,
        devicePublicKey: hello.devicePublicKey ?? '',
        signature: hello.signature ?? '',
        deviceSigningKey: this.opened.devicePublicKey,
      });
      const key = await deriveSessionKey({
        privateKey: ephemeral.privateKey,
        peerPublicKey: hello.devicePublicKey ?? '',
        sessionId: this.sessionId,
      });
      this.deviceKeyId = await keyIdOf(this.opened.devicePublicKey);
      this.channel = new LiveChannel(key, this.sessionId, 'b2d');
    } catch (error) {
      throw cryptoError(error);
    } finally {
      // The private half did its one job; it never needs to exist again.
      this.ephemeral = null;
    }
    if (this.polls.signal.aborted) throw new LiveViewError('aborted', 'The live view closed.');
    this.ready = true;
    this.expiry = setTimeout(
      () =>
        void this.finish(
          new LiveViewError(
            'expired',
            'The live view ran out of time. A session lasts at most 60 minutes; open it again to go on.'
          )
        ),
      Math.max(0, this.closesAt - Date.now())
    );
    void this.receive();
  }

  private async waitForHello(): Promise<LiveRelayMessage> {
    const deadline = Date.now() + HELLO_TIMEOUT_MS;
    for (;;) {
      if (Date.now() > deadline)
        throw new LiveViewError(
          'no_answer',
          'The practice computer did not answer. It may be asleep or busy with an import. Try again in a few minutes.'
        );
      const [first, ...rest] = await this.poll();
      if (!first) continue;
      if (first.kind === 'close')
        throw new LiveViewError(
          'refused',
          'The practice computer turned the live view down, because the practice has switched it off.'
        );
      if (first.kind !== 'hello' || first.seq !== 0)
        throw protocolError('The first message was not the signed hello.');
      this.lastSeq = 0;
      this.backlog = rest;
      return first;
    }
  }

  private async poll(): Promise<LiveRelayMessage[]> {
    const params: Record<string, number> = { wait: POLL_WAIT_SECONDS };
    if (this.lastSeq !== null) params.after = this.lastSeq;
    for (let attempt = 1; ; attempt++) {
      try {
        const response = await this.http.get(`${this.base}/messages`, {
          params,
          signal: this.polls.signal,
          timeout: POLL_TIMEOUT_MS,
        });
        return relayMessages(response.data);
      } catch (error) {
        if (this.polls.signal.aborted) throw new LiveViewError('aborted', 'The live view closed.');
        if (error instanceof LiveViewError) throw error;
        const status = statusOf(error);
        if (status === 404 || status === 409 || status === 410)
          throw new LiveViewError('ended', 'Rudi ended the live view session.');
        const transient = status === undefined || status >= 500 || status === 408 || status === 429;
        if (!transient)
          throw new LiveViewError('failed', rudiText(error, 'Rudi refused the live view relay.'));
        if (attempt >= POLL_ATTEMPTS)
          throw new LiveViewError(
            'relay',
            "Cortex lost the connection to Rudi's relay, so it closed the live view."
          );
        await delay(1000 * attempt, this.polls.signal);
      }
    }
  }

  private async receive(): Promise<void> {
    try {
      while (!this.ended) {
        const messages = this.backlog.length ? this.backlog.splice(0) : await this.poll();
        for (const message of messages) {
          if (this.ended) return;
          await this.handle(message);
        }
      }
    } catch (error) {
      if (error instanceof LiveViewError && error.code === 'aborted') return;
      await this.finish(error instanceof LiveViewError ? error : cryptoError(error));
    }
  }

  private async handle(message: LiveRelayMessage): Promise<void> {
    if (message.kind === 'close') {
      await this.finish(
        new LiveViewError('ended', 'The practice computer closed the live view.'),
        false
      );
      return;
    }
    if (
      message.kind !== 'data' ||
      typeof message.iv !== 'string' ||
      typeof message.ciphertext !== 'string'
    )
      throw protocolError('A second hello arrived, or a data message without ciphertext.');
    const channel = this.channel;
    if (!channel) return;
    let text: string;
    try {
      text = await channel.open({
        seq: message.seq,
        iv: message.iv,
        ciphertext: message.ciphertext,
      });
    } catch (error) {
      throw cryptoError(error);
    }
    this.lastSeq = message.seq;
    let answer: unknown;
    try {
      answer = JSON.parse(text);
    } catch {
      throw protocolError('An answer was not JSON.');
    }
    this.deliver(answer);
  }

  private deliver(answer: unknown): void {
    const pending = this.pending;
    if (!pending) throw protocolError('An answer arrived that nobody asked for.');
    if (!isRecord(answer) || typeof answer.op !== 'string')
      throw protocolError('An answer had no op.');
    this.pending = null;
    clearTimeout(pending.timer);
    if (answer.op === 'error') {
      pending.reject(
        new LiveDeviceError(typeof answer.errorClass === 'string' ? answer.errorClass : 'unknown')
      );
      return;
    }
    if (answer.op !== pending.expect) {
      const error = protocolError(`It answered ${answer.op} to a ${pending.expect} request.`);
      pending.reject(error);
      throw error;
    }
    pending.resolve(answer);
  }

  /** One POST at a time, so the relay never sees number n + 1 before n. */
  private post(request: LiveRequest, channel: LiveChannel): Promise<void> {
    const run = async () => {
      const sealed = await channel.seal(JSON.stringify(request));
      await this.http.post(`${this.base}/messages`, {
        seq: sealed.seq,
        kind: 'data',
        iv: sealed.iv,
        ciphertext: sealed.ciphertext,
      } satisfies LiveRelayMessage);
    };
    const result = this.posts.then(run, run);
    this.posts = result.catch(() => undefined);
    return result;
  }

  /** One request at a time: answers carry no request ID, so they match in order. */
  private request(
    request: LiveRequest,
    expect: AnswerOp,
    before?: () => Promise<void>
  ): Promise<Record<string, unknown>> {
    const run = async () => {
      if (this.ended || !this.channel)
        throw new LiveViewError('closed', 'The live view is closed.');
      if (before) await before();
      const channel = this.channel;
      if (this.ended || !channel) throw new LiveViewError('closed', 'The live view is closed.');
      const answer = new Promise<Record<string, unknown>>((resolve, reject) => {
        this.pending = {
          expect,
          resolve,
          reject,
          timer: setTimeout(
            () =>
              void this.finish(
                new LiveViewError(
                  'no_answer',
                  'The practice computer stopped answering, so Cortex closed the live view.'
                )
              ),
            ANSWER_TIMEOUT_MS
          ),
        };
      });
      try {
        await this.post(request, channel);
      } catch (error) {
        const status = statusOf(error);
        const ended =
          status === 404 || status === 409 || status === 410
            ? new LiveViewError('ended', 'Rudi ended the live view session.')
            : new LiveViewError(
                'relay',
                "Cortex could not hand a request to Rudi's relay, so it closed the live view."
              );
        void this.finish(ended);
        throw ended;
      }
      return answer;
    };
    const result = this.requests.then(run, run);
    this.requests = result.catch(() => undefined);
    return result;
  }

  async runs(): Promise<LiveRun[]> {
    const answer = await this.request({ op: 'runs' }, 'runs');
    return Array.isArray(answer.runs) ? (answer.runs as LiveRun[]) : [];
  }

  async rows(input: {
    runId: string;
    cursor?: string;
    excludedSample?: boolean;
  }): Promise<LiveRowsAnswer> {
    const request: LiveRequest = { op: 'rows', runId: input.runId };
    if (input.cursor) request.cursor = input.cursor;
    if (input.excludedSample) request.excludedSample = true;
    const answer = await this.request(request, 'rows');
    return {
      op: 'rows',
      runId: typeof answer.runId === 'string' ? answer.runId : input.runId,
      funnel: Array.isArray(answer.funnel) ? (answer.funnel as LiveRowsAnswer['funnel']) : [],
      rows: Array.isArray(answer.rows) ? (answer.rows as LiveRowsAnswer['rows']) : [],
      nextCursor: typeof answer.nextCursor === 'string' ? answer.nextCursor : null,
    };
  }

  async sql(statement: string): Promise<LiveSqlResult> {
    const answer = await this.request({ op: 'sql', statement }, 'sql_result', async () => {
      try {
        await this.http.post(`${this.base}/log`, { kind: 'sql', statement } satisfies LiveLogEntry);
      } catch {
        throw new LiveViewError(
          'log_failed',
          'Cortex could not log the statement, so it did not send it.'
        );
      }
    });
    return sqlResult(answer);
  }

  async close(): Promise<void> {
    await this.finish(null);
  }

  /**
   * Ends the session once. Drops the key, stops polling, fails a waiting
   * request, and by default tells the practice computer and Rudi.
   */
  async finish(error: LiveViewError | null, tell = true): Promise<void> {
    if (this.ended) return;
    this.ended = true;
    clearTimeout(this.expiry);
    this.polls.abort();
    this.backlog = [];
    this.ephemeral = null;
    const pending = this.pending;
    this.pending = null;
    if (pending) {
      clearTimeout(pending.timer);
      pending.reject(error ?? new LiveViewError('closed', 'The live view is closed.'));
    }
    const channel = this.channel;
    this.channel = null;
    if (this.ready) this.options.onEnd?.(error);
    if (tell && channel) {
      try {
        await this.post({ op: 'close' }, channel);
      } catch {
        // The relay may be gone already; Rudi's close below covers it.
      }
    }
    channel?.destroy();
    try {
      await this.http.post(`${this.base}/close`);
    } catch {
      // Rudi ends the session on its own at expiresAt.
    }
  }
}

/**
 * Opens a live view on a practice computer and resolves once the computer's
 * signed hello checked out and both sides hold the session key.
 */
export async function openLiveSession(
  http: LiveHttp,
  input: { deviceId: string; runId?: string },
  options: OpenLiveSessionOptions = {}
): Promise<LiveSession> {
  let ephemeral: EphemeralKey;
  try {
    ephemeral = await generateEphemeralKey();
  } catch (error) {
    throw cryptoError(error);
  }
  if (options.signal?.aborted) throw new LiveViewError('aborted', 'The live view closed.');
  const body: LiveOpenRequest = { deviceId: input.deviceId, browserPublicKey: ephemeral.publicKey };
  if (input.runId) body.runId = input.runId;
  let opened: LiveOpenAnswer;
  try {
    opened = (await http.post(LIVE_PATH, body, { signal: options.signal })).data as LiveOpenAnswer;
  } catch (error) {
    if (options.signal?.aborted) throw new LiveViewError('aborted', 'The live view closed.');
    throw openError(error);
  }
  if (
    !isRecord(opened) ||
    typeof opened.sessionId !== 'string' ||
    !SESSION_ID.test(opened.sessionId) ||
    typeof opened.devicePublicKey !== 'string'
  )
    throw new LiveViewError(
      'protocol',
      'Rudi answered the live view request in a form Cortex does not know.'
    );
  const session = new Session(http, opened, ephemeral, options);
  try {
    await session.start();
  } catch (error) {
    await session.finish(null);
    throw error instanceof LiveViewError ? error : cryptoError(error);
  }
  return session;
}
